import { computed, ref, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  currentDevIdentity,
  setDevIdentity,
} from '@/services/slackClient'
import {
  buildEndSessionUrl,
  sanitiseReturnTo,
  startLogin,
  type AuthentikUserInfo,
} from '@/services/auth/oidc'
import { readSsoConfig } from '@/services/auth/config'
import {
  clearSession,
  clearTokens,
  hasUsableAccessToken,
  isAllowedDomain,
  readSession,
  readTokens,
  saveSession,
  saveTokens,
  type AuthSession,
  type StoredTokens,
} from '@/services/auth/session'

export type AuthMode = 'sso' | 'dev'

export interface AuthedUser {
  sub: string
  email?: string
  name?: string
  userId: string
  locationId: string
  /** Authentik groups (e.g. ['gohighlevel/eng/platform']) — used for UI subtitles + future RBAC. */
  groups?: string[]
}

const DEV_SIGNED_IN_KEY = 'teamofone.devSignedIn'
const POST_LOGOUT_HOME_PATH = '/login'

// Module-level singleton — both cookie + localStorage live outside Vue
// reactivity so we bump a version counter when we mutate them.
const version = ref(0)

function readMode(): AuthMode {
  const raw = (import.meta.env.VITE_TEAMOFONE_AUTH_MODE as string | undefined) ?? 'sso'
  return raw === 'dev' ? 'dev' : 'sso'
}

export function useAuth(): {
  isAuthed: ComputedRef<boolean>
  user: ComputedRef<AuthedUser | null>
  authMode: AuthMode
  login: (returnTo?: string) => Promise<void>
  logout: () => Promise<void>
  /** Persist a fresh user + tokens after token-exchange + userinfo. */
  applySsoSession: (user: AuthentikUserInfo, tokens: StoredTokens) => void
  /** Dev-mode credential capture — stores identity in localStorage. */
  devSignIn: (input: { username: string; locationId?: string }) => void
} {
  const router = useRouter()
  const route = useRoute()
  const authMode = readMode()

  const session = computed<AuthSession | null>(() => {
    void version.value
    if (authMode === 'dev') return null
    return readSession()
  })

  const isAuthed = computed<boolean>(() => {
    void version.value
    if (authMode === 'dev') {
      return localStorage.getItem(DEV_SIGNED_IN_KEY) === '1'
    }
    return session.value !== null && hasUsableAccessToken()
  })

  const user = computed<AuthedUser | null>(() => {
    void version.value
    if (authMode === 'dev') {
      if (localStorage.getItem(DEV_SIGNED_IN_KEY) !== '1') return null
      const id = currentDevIdentity()
      return {
        sub: id.userId,
        userId: id.userId,
        locationId: id.locationId,
        email: id.userId.includes('@') ? id.userId : undefined,
        name: undefined,
      }
    }
    const s = session.value
    if (!s || !hasUsableAccessToken()) return null
    return {
      sub: s.id,
      email: s.email,
      name: s.name,
      userId: s.id,
      locationId: s.locationId ?? 'loc-dev',
      groups: s.groups,
    }
  })

  async function login(returnTo?: string): Promise<void> {
    const target = sanitiseReturnTo(
      returnTo ?? (route.query.returnTo as string) ?? '/',
    )
    if (authMode === 'dev') {
      await router.push(target)
      return
    }
    await startLogin(readSsoConfig(), target)
  }

  async function logout(): Promise<void> {
    clearSession()
    clearTokens()
    localStorage.removeItem(DEV_SIGNED_IN_KEY)
    version.value++
    const pushPostLogoutHome = async (): Promise<void> => {
      const current = router.currentRoute.value
      if (
        current.path === POST_LOGOUT_HOME_PATH &&
        Object.keys(current.query).length === 0 &&
        !current.hash
      ) {
        return
      }
      await router.push(POST_LOGOUT_HOME_PATH)
    }
    await pushPostLogoutHome()
    if (authMode === 'dev') {
      return
    }
    const cfg = readSsoConfig()
    const back = `${window.location.origin}${POST_LOGOUT_HOME_PATH}`
    try {
      window.location.assign(buildEndSessionUrl(cfg, back))
    } catch {
      await pushPostLogoutHome()
    }
  }

  function applySsoSession(
    info: AuthentikUserInfo,
    tokens: StoredTokens,
  ): void {
    if (!isAllowedDomain(info.email)) {
      throw new Error(
        `Access restricted: ${info.email ?? 'unknown email'} is not on the allowed domain.`,
      )
    }
    const sessionRow: AuthSession = {
      id: info.sub,
      email: info.email ?? '',
      name: info.name ?? info.preferred_username ?? info.email ?? 'User',
      emailVerified: info.email_verified,
      groups: info.groups,
      locationId: 'loc-dev', // Authentik doesn't carry GHL locationId today
    }
    saveSession(sessionRow)
    saveTokens(tokens)
    // Mirror identity into the legacy headers so existing API clients
    // stay happy without a refactor.
    setDevIdentity({
      userId: info.email ?? info.sub,
      locationId: sessionRow.locationId ?? 'loc-dev',
    })
    version.value++
  }

  function devSignIn(input: { username: string; locationId?: string }): void {
    const userId = input.username.trim()
    const locationId = (input.locationId ?? '').trim() || 'loc-dev'
    setDevIdentity({ userId, locationId })
    localStorage.setItem(DEV_SIGNED_IN_KEY, '1')
    version.value++
  }

  return {
    isAuthed,
    user,
    authMode,
    login,
    logout,
    applySsoSession,
    devSignIn,
  }
}

/** Bump the version counter — used by the 401 listener on session loss. */
export function notifyAuthChanged(): void {
  version.value++
}

/** Re-export for non-component callers (the router guard). */
export { readSession, readTokens, clearSession, clearTokens }
